import dayjs from 'dayjs'
import { FileOutlined } from '@ant-design/icons'
import React from 'react'

class Utils {
  /**
   * Get first character from first & last sentences of a username
   * @param {String} name - Username
   * @return {String} 2 characters string
   */
  static getNameInitial(name) {
    let initials = name.match(/\b\w/g) || []
    return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase()
  }

  /**
   * Get current path related object from Navigation Tree
   * @param {Array} navTree - Navigation Tree from directory 'configs/NavigationConfig'
   * @param {String} path - Location path you looking for e.g '/app/dashboards/analytic'
   * @return {Object} object that contained the path string
   */
  static getRouteInfo(navTree, path) {
    if (navTree?.path === path) {
      return navTree
    }
    let route
    for (let p in navTree) {
      if (navTree.hasOwnProperty(p) && typeof navTree[p] === 'object') {
        route = this.getRouteInfo(navTree[p], path)
        if (route) {
          return route
        }
      }
    }

    return route
  }

  /**
   * Get accessible color contrast
   * @param {String} hex - Hex color code e.g '#3e82f7'
   * @return {String} 'dark' or 'light'
   */
  static getColorContrast(hex) {
    if (!hex) {
      return 'dark'
    }
    const threshold = 130
    const hRed = hexToR(hex)
    const hGreen = hexToG(hex)
    const hBlue = hexToB(hex)

    function hexToR(h) {
      return parseInt(cutHex(h).substring(0, 2), 16)
    }

    function hexToG(h) {
      return parseInt(cutHex(h).substring(2, 4), 16)
    }

    function hexToB(h) {
      return parseInt(cutHex(h).substring(4, 6), 16)
    }

    function cutHex(h) {
      return h.charAt(0) === '#' ? h.substring(1, 7) : h
    }

    const cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000
    if (cBrightness > threshold) {
      return 'dark'
    } else {
      return 'light'
    }
  }

  /**
   * Darken or lighten a hex color
   * @param {String} color - Hex color code e.g '#3e82f7'
   * @param {Number} percent - Percentage -100 to 100, positive for lighten, negative for darken
   * @return {String} Darken or lighten color
   */
  static shadeColor(color, percent) {
    let R = parseInt(color.substring(1, 3), 16)
    let G = parseInt(color.substring(3, 5), 16)
    let B = parseInt(color.substring(5, 7), 16)
    R = parseInt((R * (100 + percent)) / 100)
    G = parseInt((G * (100 + percent)) / 100)
    B = parseInt((B * (100 + percent)) / 100)
    R = R < 255 ? R : 255
    G = G < 255 ? G : 255
    B = B < 255 ? B : 255
    const RR = R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16)
    const GG = G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16)
    const BB = B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16)
    return `#${RR}${GG}${BB}`
  }

  /**
   * Returns either a positive or negative
   * @param {Number} number - number value
   * @param {any} positive - value that return when positive
   * @param {any} negative - value that return when negative
   * @return {any} positive or negative value based on param
   */
  static getSignNum(number, positive, negative) {
    if (number > 0) {
      return positive
    }
    if (number < 0) {
      return negative
    }
    return null
  }

  /**
   * Returns either ascending or descending value
   * @param {Object} a - antd Table sorter param a
   * @param {Object} b - antd Table sorter param b
   * @param {String} key - object key for compare
   * @return {any} a value minus b value
   */
  static antdTableSorter(a, b, key) {
    if (typeof a[key] === 'number' && typeof b[key] === 'number') {
      return a[key] - b[key]
    }

    if (typeof a[key] === 'string' && typeof b[key] === 'string') {
      a = a[key].toLowerCase()
      b = b[key].toLowerCase()
      return a > b ? -1 : b > a ? 1 : 0
    }
    return
  }

  /**
   * Filter array of object
   * @param {Array} list - array of objects that need to filter
   * @param {String} key - object key target
   * @param {any} value  - value that excluded from filter
   * @return {Array} a value minus b value
   */
  static filterArray(list, key, value) {
    let data = list
    if (list) {
      data = list.filter(item => item[key] === value)
    }
    return data
  }

  /**
   * Remove object from array by value
   * @param {Array} list - array of objects
   * @param {String} key - object key target
   * @param {any} value  - target value
   * @return {Array} Array that removed target object
   */
  static deleteArrayRow(list, key, value) {
    let data = list
    if (list) {
      data = list.filter(item => item[key] !== value)
    }
    return data
  }

  /**
   * Wild card search on all property of the object
   * @param {Number | String} input - any value to search
   * @param {Array} list - array for search
   * @return {Array} array of object contained keyword
   */
  static wildCardSearch(list, input) {
    const searchText = item => {
      for (let key in item) {
        if (item[key] == null) {
          continue
        }
        if (item[key].toString().toUpperCase().indexOf(input.toString().toUpperCase()) !== -1) {
          return true
        }
      }
    }
    list = list.filter(value => searchText(value))
    return list
  }

  /**
   * Get Breakpoint
   * @param {Object} screens - Grid.useBreakpoint() from antd
   * @return {Array} array of breakpoint size
   */
  static getBreakPoint(screens) {
    let breakpoints = []
    for (const key in screens) {
      if (screens.hasOwnProperty(key)) {
        const element = screens[key]
        if (element) {
          breakpoints.push(key)
        }
      }
    }
    return breakpoints
  }

  // 👉 IsEmpty
  static isEmpty = value => {
    if (value === null || value === undefined || value === '') return true

    return !!(Array.isArray(value) && value.length === 0)
  }

  // 👉 IsEmpty
  static isEmptyObject = value => {
    if (value === null || value === undefined || value === '') return true

    return Object.keys(value).length === 0
  }

  // 👉 IsNullOrUndefined
  static isNullOrUndefined = value => {
    return value === null || value === undefined
  }

  // 👉 IsEmptyArray
  static isEmptyArray = arr => {
    return Array.isArray(arr) && arr.length === 0
  }

  // 👉 IsObject
  static isObject = obj => obj !== null && !!obj && typeof obj === 'object' && !Array.isArray(obj)
  static isToday = date => {
    const today = new Date()

    return (
      /* eslint-disable operator-linebreak */
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
      /* eslint-enable */
    )
  }

  static deleteZeroWithDate = (dateStr, format = 'YYYY-MM-DD') => {
    if (Utils.isEmpty(dateStr)) return ''
    // '-/.' 문자를 모두 제거
    let cleanedStr = dateStr.replaceAll(/[-/.]/g, '')
    // 결과가 '0'으로만 구성 되어 있다면 공백을 반환, 아니면 cleanedStr 반환
    if (cleanedStr.split('').every(char => char === '0')) return ''

    return dayjs(cleanedStr).format(format)
  }

  static formatTime = timeString => {
    if (timeString && timeString.length === 6) {
      // 시간, 분, 초를 각각 추출합니다.
      let hours = timeString.substring(0, 2)
      let minutes = timeString.substring(2, 4)
      let seconds = timeString.substring(4, 6)

      // HH:MM:SS 형식으로 반환합니다.
      return `${hours}:${minutes}:${seconds}`
    } else {
      // 잘못된 입력에 대한 처리를 합니다.
      return ''
    }
  }

  static createTree = data => {
    const map = {}
    data.forEach(item => {
      map[item.key] = { ...item }
    })

    const treeData = []
    data.forEach(item => {
      const { parentKey } = item
      if (parentKey && map[parentKey]) {
        if (!map[parentKey].children) map[parentKey].children = []
        map[parentKey].children.push(map[item.key])
      } else {
        treeData.push(map[item.key])
      }
    })

    // children에 level과 sortOrder 추가
    const assignLevelAndSortOrder = (nodes, level = 1) => {
      nodes.forEach((node, index) => {
        node.level = level
        node.sortOrder = index + 1

        if (node.children && node.children.length > 0) {
          // 그리고 다음 레벨의 노드들에 대해 함수를 재귀적으로 호출합니다
          assignLevelAndSortOrder(node.children, level + 1)
        }
      })
    }

    assignLevelAndSortOrder(treeData) // 최상위 노드부터 level과 sortOrder 할당

    return treeData // 새로운 트리 데이터를 반환합니다.
  }

  // 부모를 찾는 함수
  static findParent = (data, key) => {
    let parent
    data.some(node => {
      if (node.children) {
        if (node.children.some(child => child.key === key)) {
          parent = node
          return true
        }
        parent = this.findParent(node.children, key)
        return parent
      }
      return false
    })

    return parent
  }

  static addCommasToNumber = (number, fixed) => {
    if (Utils.isEmpty(number)) {
      return ''
    }

    let roundedNum = parseFloat(number)

    roundedNum = !fixed
      ? roundedNum.toLocaleString()
      : roundedNum.toLocaleString(undefined, {
          minimumFractionDigits: fixed,
          maximumFractionDigits: fixed,
        })

    return roundedNum === '0' ? '' : roundedNum
  }

  static addCommasToNumberWithZero = (number, fixed) => {
    if (Utils.isEmpty(number)) {
      return ''
    }

    let roundedNum = parseFloat(number)

    roundedNum = !fixed
      ? roundedNum.toLocaleString()
      : roundedNum.toLocaleString(undefined, {
          minimumFractionDigits: fixed,
          maximumFractionDigits: fixed,
        })

    return roundedNum
  }

  /**
   * 콤마가 포함된 숫자String을 number타입으로 변환
   * 소수점.이 포함되어있다면 toFloat처리
   * @param numStr
   * @returns {number}
   */
  static toNumber(numStr) {
    if (typeof numStr !== 'string') return numStr

    const temp = numStr.replace(/,/g, '')
    return temp.includes('.') ? parseFloat(temp) : parseInt(temp)
  }

  // null, undefined, '' 값은 null로 리턴하고 그외 값은 value를 리턴한다.
  static normalizeValue = value => {
    if (value === null || value === undefined || value === '') return null

    return value
  }

  static upperAlphaNumber = value => {
    return value.replace(/[^a-zA-Z0-9-\s]/gi, '').toUpperCase()
  }

  static alphaNumber = value => {
    return value.replace(/[^a-zA-Z0-9-\s]/gi, '')
  }

  static number = value => {
    return value.replace(/[^0-9]/gi, '')
  }

  static upperAlpha = value => {
    return value.replace(/[^a-zA-Z\s]/gi, '').toUpperCase()
  }

  static upperText = value => {
    return value.toUpperCase()
  }

  static numberDot = value => {
    return value.replace(/[^0-9.]/gi, '')
  }

  static numberDotSign = value => {
    return value.replace(/[^0-9.-]/gi, '')
  }

  static numberSign = value => {
    return value.replace(/[^0-9-]/gi, '')
  }

  static strPartNo = value => {
    if (Utils.isEmpty(value)) return ''

    return value.substring(0, 5) + '-' + value.substring(5)
  }

  static nonStrPartNo = value => {
    if (Utils.isEmpty(value)) return ''

    return value.replace(/-/g, '')
  }

  /**
   * 첨부파일 여부 표시 아이콘
   */
  static isAttathment = value => {
    if (value === 'Y') {
      return <FileOutlined />
    }
  }

  static convertBlobToJson = blob => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = function (event) {
        try {
          const jsonObj = JSON.parse(event.target.result)
          resolve(jsonObj)
        } catch (e) {
          reject(e)
        }
      }

      reader.onerror = function () {
        reject(this)
      }

      reader.readAsText(blob)
    })
  }

  static getLastUrl(url) {
    if (Utils.isEmpty(url)) return
    const segments = url.split('/') // URL을 '/' 기준으로 나눕니다.
    return segments.pop() || '' // 마지막 세그먼트를 반환하거나, 없으면 빈 문자열 반환
  }

  /**
   * FormData 멀티파트 전송 시, data를 Form으로 변환 해 주는 함수
   * @param data 추가 할 데이터 오브젝트
   * @param form 추가 할 폼
   * @param namespace 재귀용 네임스페이스 파라미터 할당 X
   * @returns {*}
   *
   * ex) const datas = {voList: selectedDatas,condVO: values}
   *     Backend VO :
   *     private EKD01030CondVO condVO;
   *     private List<EKD01030VO> voList;
   */
  static objToFormData({ data, form = new FormData(), namespace = '' }) {
    Object.keys(data).forEach(key => {
      const value = data[key]
      const formKey = namespace ? `${namespace}.${key}` : key

      if (!value) return

      if (data[key] instanceof Date) {
        form.append(formKey, data[key].toISOString())
      } else if (value instanceof File) {
        form.append(formKey, value)
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          const tempFormKey = `${formKey}[${index}]`
          if (typeof item === 'object' && !(item instanceof File)) {
            this.objToFormData({ data: item, form: form, namespace: tempFormKey })
          } else {
            form.append(tempFormKey, item)
          }
        })
      } else if (typeof data[key] === 'object' && !(data[key] instanceof File)) {
        this.objToFormData({ data: data[key], form: form, namespace: formKey })
      } else {
        form.append(formKey, data[key])
      }
    })
    return form
  }

  static onDoubleClick = obj => {
    // 더블클릭 이벤트를 생성합니다.
    const event = new MouseEvent('dblclick', {
      bubbles: true, // 이벤트가 상위 요소로 전파됨
      cancelable: true, // 이벤트를 취소할 수 있음
      view: window, // 이벤트와 연관된 view는 window 객체
    })

    // 요소에 더블클릭 이벤트를 디스패치합니다.
    obj.dispatchEvent(event)
  }
}

export default Utils
